export const ARTICLES = [
  {
    slug: "data-breach-real-cost-small-business",
    tag: "Cybersecurity",
    readTime: "4 min read",
    published: "2026-07-13",
    title: "The $10 Million Mistake: What a Data Breach Really Costs a US Business",
    excerpt:
      "IBM's latest research puts the average US breach at $10.22 million. Here's why enterprise-grade IT is the cheaper line item — by an order of magnitude.",
    metaTitle: "What a Data Breach Costs a Small Business | Sempresera Insights",
    metaDescription:
      "IBM reports the average US data breach costs $10.22 million and takes 241 days to contain. Why small businesses can't afford to wing IT — and what actually prevents breaches.",
    body: [
      {
        h: "The number that ends businesses",
        p: [
          "IBM's Cost of a Data Breach Report 2025 puts the average cost of a breach for a US organization at $10.22 million — the highest of any country — with a global average of $4.44 million. The FBI's IC3 recorded $16.6 billion in reported cybercrime losses in 2024 alone, a 33% jump year over year.",
          "Small businesses often assume those numbers belong to enterprises. They don't. Attackers automate everything now; a ten-person firm is scanned, phished and probed by the same industrial-scale tooling that hits banks. The difference is the bank has a security operations center. Most small businesses have a prayer.",
        ],
        stat: { value: "$10.22M", label: "average cost of a data breach for a US organization", source: "IBM Cost of a Data Breach Report 2025" },
      },
      {
        h: "Breaches are slow, not sudden",
        p: [
          "The same IBM research found the average breach takes 241 days to identify and contain. That's eight months of an attacker reading email, copying files and staging ransomware while nobody watches the logs.",
          "This is the single strongest argument for 24/7 monitoring. Enterprise-grade endpoint detection with a human-led SOC — the kind Sempresera deploys through Huntress — exists to shrink those 241 days to minutes. Every day shaved off detection is money, reputation and client trust preserved.",
        ],
        stat: { value: "241 days", label: "average time to identify and contain a breach", source: "IBM Cost of a Data Breach Report 2025" },
      },
      {
        h: "The math small businesses should actually run",
        p: [
          "Compare two line items. Line one: a managed IT standard that includes monitoring, patching, security operations and tested backups — a predictable per-user monthly cost. Line two: incident response lawyers, forensics, regulatory notification, downtime, ransom negotiations and customer churn after an incident.",
          "Enterprise-grade IT isn't a premium purchase. It's the discount version of a breach.",
        ],
      },
      {
        h: "What to do this quarter",
        list: [
          "Put every device under 24/7 monitored EDR — not just antivirus",
          "Turn on multi-factor authentication everywhere, enforced by conditional access",
          "Test a real restore of your most critical data — don't assume the backup works",
          "Ask your provider one question: \"Who is watching our environment at 3am on a Sunday?\"",
        ],
      },
    ],
    faqs: [
      {
        q: "How much does a data breach cost a small business?",
        a: "IBM's Cost of a Data Breach Report 2025 measured the average US breach at $10.22 million across detection, response, downtime and lost business. Even incidents far below that average routinely exceed what a small business spends on IT in several years.",
      },
      {
        q: "What's the most effective first step to reduce breach risk?",
        a: "Monitored endpoint detection and response (EDR) with a human security operations center, enforced multi-factor authentication, and tested backups. Together they address how breaches start, how long they last, and how fast you recover.",
      },
    ],
  },
  {
    slug: "human-element-cybersecurity",
    tag: "Security Awareness",
    readTime: "4 min read",
    published: "2026-07-13",
    title: "Two-Thirds of Breaches Start With a Person, Not a Machine",
    excerpt:
      "Verizon's DBIR keeps finding the same thing: the human element opens the door. The fix isn't blame — it's training, identity protection and sensible guardrails.",
    metaTitle: "The Human Element in Cybersecurity | Sempresera Insights",
    metaDescription:
      "Verizon's DBIR attributes roughly two-thirds of breaches to the human element — phishing, stolen credentials, errors. How small businesses can close that gap with training and identity protection.",
    body: [
      {
        h: "Your firewall isn't the front door — your inbox is",
        p: [
          "Verizon's Data Breach Investigations Report has attributed roughly two-thirds of breaches to the human element: someone clicked a convincing link, reused a password, or approved a sign-in they shouldn't have. Attackers aren't hacking systems first anymore — they're hacking people, then walking through systems with legitimate credentials.",
          "For a small business this is actually good news. You can't outspend an attacker, but you can out-train and out-guard one.",
        ],
        stat: { value: "~68%", label: "of breaches involve a human element — phishing, stolen credentials, or error", source: "Verizon Data Breach Investigations Report 2024" },
      },
      {
        h: "Training that works is short, frequent and simulated",
        p: [
          "Annual slide-deck training doesn't change behavior. What works: brief recurring security awareness training paired with real phishing simulations, so staff build the reflex to pause and verify. Sempresera includes exactly this — Huntress security awareness training with phishing simulations — in the standard plan, because the data says it's the highest-leverage security dollar a small firm can spend.",
          "Just as important: make reporting safe. The employee who says \"I think I clicked something\" within five minutes is your best intrusion sensor.",
        ],
      },
      {
        h: "Assume credentials will leak — guard the identity",
        p: [
          "Passwords will eventually be phished or exposed in some third-party breach. The backstop is identity protection: multi-factor authentication enforced by conditional access policies, dark-web monitoring for exposed credentials, and identity threat detection that spots impossible logins, inbox-rule tampering and account takeover inside Microsoft 365.",
          "This is why the Sempresera stack pairs Huntress Managed ITDR with Entra ID conditional access: the person may slip, but the account doesn't fall.",
        ],
      },
      {
        h: "What to do this quarter",
        list: [
          "Enroll every employee in ongoing awareness training with phishing simulations",
          "Enforce MFA with conditional access — no exceptions, including the owner",
          "Turn on dark-web monitoring for your company domain",
          "Practice the sentence out loud: \"reporting a suspicious click is always safe here\"",
        ],
      },
    ],
    faqs: [
      {
        q: "What is the biggest cybersecurity risk for small businesses?",
        a: "The human element. Verizon's DBIR attributes roughly two-thirds of breaches to phishing, stolen credentials and human error — which is why ongoing awareness training and identity protection outperform most tool purchases.",
      },
      {
        q: "Do phishing simulations actually work?",
        a: "Yes. Organizations that run regular simulated phishing see click rates fall sharply over time, because staff build a verify-first reflex. Simulations paired with short, frequent training are far more effective than annual courses.",
      },
    ],
  },
  {
    slug: "ransomware-backup-recovery",
    tag: "Backup & Recovery",
    readTime: "4 min read",
    published: "2026-07-13",
    title: "Ransomware Shows Up in 88% of Small-Business Breaches — Is Your Backup Actually Recoverable?",
    excerpt:
      "Verizon's 2025 DBIR found ransomware in 88% of breaches at small and midsize businesses. The difference between a bad week and a closed business is a tested, immutable backup.",
    metaTitle: "Ransomware & Backups for Small Business | Sempresera Insights",
    metaDescription:
      "Verizon's 2025 DBIR: ransomware appears in 88% of SMB breaches; Sophos puts average recovery cost at $2.73M. Why immutable, tested backups are the last line that can't fail.",
    body: [
      {
        h: "The stat that should end the debate",
        p: [
          "Verizon's 2025 Data Breach Investigations Report found ransomware present in 88% of breaches at small and midsize businesses — dramatically higher than at large enterprises. Attackers know smaller firms are less likely to have immutable backups, tested recovery plans, or anyone watching at night.",
          "Sophos's State of Ransomware 2024 put the average recovery cost at $2.73 million — and that figure excludes the ransom itself. The uncomfortable truth: in a ransomware event, your backup isn't a feature. It is the entire business plan.",
        ],
        stat: { value: "88%", label: "of breaches at small and midsize businesses involve ransomware", source: "Verizon Data Breach Investigations Report 2025" },
      },
      {
        h: "\"We have backups\" usually means \"we have hope\"",
        p: [
          "Most backup setups fail in one of three ways: they're connected to the network and get encrypted along with everything else; they quietly stopped running months ago; or they've never been restore-tested, so nobody knows if they work.",
          "A ransomware-proof strategy needs three properties. Immutable copies with object lock, so even an attacker with your credentials can't encrypt or delete the backups. Cloud-to-cloud backup of Microsoft 365 — Microsoft does not back up your Exchange, OneDrive or SharePoint data for you. And scheduled restore tests with written results, because an untested backup is a rumor.",
        ],
        stat: { value: "$2.73M", label: "average cost to recover from a ransomware attack, excluding any ransom", source: "Sophos State of Ransomware 2024" },
      },
      {
        h: "Recovery is a drill, not a document",
        p: [
          "Sempresera's standard includes MSP360-powered immutable daily backups, Microsoft 365 cloud-to-cloud backup, quarterly restore tests with reported results, and an annual disaster recovery drill against a documented plan. When recovery is rehearsed, ransomware becomes a bad afternoon instead of a business-ending event.",
        ],
      },
      {
        h: "What to do this quarter",
        list: [
          "Ask for proof your backups are immutable (object lock) — not just \"off-site\"",
          "Confirm Microsoft 365 mail, files and Teams data are backed up independently of Microsoft",
          "Request the results of your last restore test — in writing",
          "Write down who does what in the first hour after a ransomware discovery",
        ],
      },
    ],
    faqs: [
      {
        q: "How common is ransomware for small businesses?",
        a: "Very. Verizon's 2025 DBIR found ransomware in 88% of breaches at small and midsize businesses, and Sophos measured average recovery costs at $2.73 million. Small firms are targeted precisely because attackers expect weaker backup and monitoring practices.",
      },
      {
        q: "Isn't Microsoft 365 data backed up automatically?",
        a: "No. Under Microsoft's shared responsibility model, Microsoft protects the platform's uptime — not your data. Deleted mailboxes, ransomware-encrypted SharePoint libraries and lost OneDrive files are your responsibility unless you run independent cloud-to-cloud backup.",
      },
    ],
  },
];
